import { useSendOfferLetterMutation } from '@/app/features/recruitment/hiring-pipeline/hiring-pipeline.api';
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import SingleFileUploader from '@/components/SingleFileUploader';
import useAuth from '@/hooks/useAuth';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Flex, Form, Input } from 'antd';
import { useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

type Props = CustomModalProps & { data?: any };

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'align',
  'link',
];

export default function SendOfferLetter({ open, handleClose, data }: Props) {
  const [form] = Form.useForm();
  const [sendOfferLetter, { isLoading }] = useSendOfferLetterMutation();
  const { profileInfo } = useAuth();

  const handleClose_ = () => {
    handleClose();
    form.resetFields();
  };

  useEffect(() => {
    const applicantName = data?.applicant_name || '[Applicant Name]';
    const jobTitle = data?.job_title || '[Job Title]';
    const companyName = profileInfo?.organization?.name || '[Company Name]';

    const template = `
        <p>Dear <strong>${applicantName}</strong>,</p>
        <p>Congratulations! We are delighted to formally offer you the position of <strong>${jobTitle}</strong> at <strong>${companyName}</strong>. Based on your interview and background, we believe your skills and experience will be a fantastic addition to our team.</p>
        <p><strong>Position Details:</strong></p>
        <ul>
          <li><strong>Role:</strong> ${jobTitle}</li>
        </ul>
        <p>Please review the attached formal offer letter for detailed terms and conditions of your employment.</p>
        <p>We are excited about the prospect of you joining us and look forward to welcoming you to the team!</p>
        <p>Best regards,<br/><strong>The Recruitment Team</strong><br/>${companyName}</p>
      `;

    form.setFieldsValue({
      offer_letter_email_title: `Offer Letter - ${jobTitle}`,
      email_body: template,
    });
  }, [open, data, form, profileInfo]);

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        ...values,
        job_application_id: data?.job_application_id,
      };

      const res = await sendOfferLetter(payload).unwrap();

      if (res?.success) {
        toast(
          'success',
          getErrorMessage(res, 'Offer letter sent successfully'),
        );
        handleClose_();
      } else {
        toast('error', getErrorMessage(res, 'Failed to send offer letter'));
      }
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to send offer letter'));
    }
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose_}
      title={`${data?.applicant_name ?? ''} Offer Letter`}
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        className="custom-form space-y-6"
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Offer Letter Title"
          name="offer_letter_email_title"
          rules={[
            { required: true, message: 'Please enter offer letter title' },
          ]}
        >
          <Input placeholder="Enter offer letter title" />
        </Form.Item>

        <Form.Item
          label="Message"
          name="email_body"
          rules={[{ required: true, message: 'Please enter message' }]}
          getValueFromEvent={(value: string) => value}
        >
          <ReactQuill
            theme="snow"
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Write the offer letter body here..."
            className="offer-letter-quill"
          />
        </Form.Item>

        <Form.Item name="email_attachment">
          <SingleFileUploader
            form={form}
            accept=".pdf"
            description="Browse"
            name="email_attachment"
            title="Drag & drop your file here"
          />
        </Form.Item>

        <Flex justify="end">
          <Button loading={isLoading} htmlType="submit" className="btn-primary">
            Send &amp; Listed for Hiring
          </Button>
        </Flex>
      </Form>
    </CustomDrawer>
  );
}
